import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import {
  convertMinor,
  getCurrencyExponent,
  languageName,
  USD_RATES,
  type SupportedCurrencyCode,
} from "@repo/api-schema";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Avatar } from "./ui/avatar";
import { CurrencyCombobox } from "./CurrencyCombobox";
import { Section } from "./ui/section";
import { cn } from "../lib/utils";
import type { CreateExpenseRequest, Expense, Group } from "../api/types";

/**
 * At or below this many participants, person pickers (payer) render as a
 * tappable avatar+name row instead of a dropdown — friendlier for the common
 * small group. Larger groups keep the compact `<select>`.
 */
const COMPACT_PEOPLE_MAX = 5;

type SplitType = "EQUAL" | "AMOUNTS" | "PERCENTAGES" | "SHARES";

interface DivisionDraft {
  participantId: string;
  /** Major-unit decimal for AMOUNTS, integer string for PERCENTAGES/SHARES. */
  value: string;
  included: boolean;
  /** Participant joined the group after this expense was created. */
  isLateJoiner?: boolean;
}

interface ItemDraft {
  key: string;
  /** Existing item id, when editing. */
  id?: string;
  label: string;
  amount: string;
  splitType: SplitType;
  divisions: DivisionDraft[];
  /**
   * Translation of this item's label into the group's language, carried from a
   * receipt scan (issue #284). Display-only — shown under the editable label so
   * the user sees the original and the translation side by side. Absent unless
   * the scan translated this row.
   */
  translatedLabel?: string;
}

interface PaymentDraft {
  key: string;
  payerId: string;
  amount: string;
  /**
   * Original group-currency `charge` carried verbatim from the existing
   * expense. Preserved across no-op edits so cross-currency apportionment
   * doesn't silently rewrite per-payer charges. `undefined` for new rows.
   */
  originalCharge?: number;
  /** Original `amount` minor units, used to detect "no payment changes". */
  originalAmount?: number;
}

function nextKey(): string {
  return crypto.randomUUID();
}

function makeDivisions(group: Group): DivisionDraft[] {
  return group.participants.map((p) => ({
    participantId: p.id,
    value: "",
    included: true,
  }));
}

function makeItem(group: Group): ItemDraft {
  return {
    key: nextKey(),
    label: "",
    amount: "",
    splitType: "EQUAL",
    divisions: makeDivisions(group),
  };
}

function makePayment(payerId: string): PaymentDraft {
  return { key: nextKey(), payerId, amount: "" };
}

/**
 * Convert a major-unit decimal string to minor units without going through
 * `Number(x) * 10**exponent`, which is FP-lossy at boundary values
 * (`1.005 * 100 === 100.49999999999999`). Splits on the decimal point and
 * builds the integer directly so '1.005' / 2 yields exactly 101 and
 * '0.290' / 3 yields exactly 290.
 *
 * Returns NaN for inputs that aren't a finite decimal number, and rounds
 * away the 4th-and-beyond fractional digits when the user enters more
 * precision than the currency allows.
 */
function toMinor(amount: string, exponent: number): number {
  const trimmed = amount.trim();
  if (trimmed === "") return Number.NaN;
  // Reject scientific notation, repeated signs, etc. up front. The form's
  // <input type="number"> mostly blocks this, but pasting can sneak it in.
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const major = Number(trimmed);
    if (!Number.isFinite(major)) return Number.NaN;
    return Math.round(major * 10 ** exponent);
  }
  const negative = trimmed.startsWith("-");
  const digits = negative ? trimmed.slice(1) : trimmed;
  const [intPart, fracPart = ""] = digits.split(".");
  const padded = (fracPart + "0".repeat(exponent)).slice(0, exponent);
  // Round-half-up on the digit after the cut.
  const cutDigit = (fracPart[exponent] ?? "0").charCodeAt(0) - 48;
  const base = Number((intPart ?? "0") + padded);
  if (!Number.isFinite(base)) return Number.NaN;
  const rounded = base + (cutDigit >= 5 ? 1 : 0);
  return negative ? -rounded : rounded;
}

function minorStepFor(exponent: number): string {
  return exponent === 0 ? "1" : `0.${"0".repeat(exponent - 1)}1`;
}

function fromMinor(minor: number, exponent: number): string {
  return (minor / 10 ** exponent).toFixed(exponent);
}

function isPositiveInteger(s: string): boolean {
  return /^\d+$/.test(s.trim());
}

/**
 * Human-readable disclosure for the auto-converted group total, e.g.
 * "Estimated at ~1 USD = 0.79 GBP.". The rate is derived from the same static
 * USD_RATES table the conversion uses, so the shown rate matches the pre-fill.
 * These rates are an approximate 2024 snapshot, hence "Estimated" — the caller
 * pairs this with an "edit to match your bank" nudge.
 */
function approxRateNote(
  from: SupportedCurrencyCode,
  to: SupportedCurrencyCode,
): string {
  // units of `to` per 1 unit of `from`, via USD.
  const rate = USD_RATES[to] / USD_RATES[from];
  // Show enough significant figures for tiny rates without a wall of zeros.
  const shown =
    rate >= 1 ? rate.toFixed(2) : Number(rate.toPrecision(3)).toString();
  return `Estimated at ~1 ${from} = ${shown} ${to}.`;
}

/**
 * Seed editor state from an existing expense. Walks each item's split
 * divisions and rehydrates the per-participant inputs so toggling a
 * participant out and back in restores the original value. Participants
 * who joined the group AFTER this expense was created are flagged with
 * `isLateJoiner` so the UI can surface that they need explicit opt-in.
 */
function seedItems(group: Group, expense: Expense): ItemDraft[] {
  const exponent = getCurrencyExponent(expense.currency);
  return expense.items.map((item) => {
    const divisionByPid = new Map(
      item.split.divisions.map((d) => [d.participantId, d]),
    );
    return {
      key: nextKey(),
      id: item.id,
      label: item.label,
      amount: fromMinor(item.cost, exponent),
      splitType: item.split.type,
      divisions: group.participants.map((p) => {
        const d = divisionByPid.get(p.id);
        if (!d) {
          return {
            participantId: p.id,
            value: "",
            included: false,
            isLateJoiner: true,
          };
        }
        let value = "";
        if ("amount" in d) value = fromMinor(d.amount, exponent);
        else if ("value" in d) value = String(d.value);
        return { participantId: p.id, value, included: true };
      }),
    };
  });
}

function seedPayments(expense: Expense): PaymentDraft[] {
  const exponent = getCurrencyExponent(expense.currency);
  return expense.payments.map((p) => ({
    key: nextKey(),
    payerId: p.participantId,
    amount: fromMinor(p.amount, exponent),
    originalCharge: p.charge,
    originalAmount: p.amount,
  }));
}

export interface ExpenseFormSubmit {
  label: string;
  currency: SupportedCurrencyCode;
  payments: CreateExpenseRequest["payments"];
  items: Array<{
    /** Present when an existing item is being kept; absent for new rows. */
    id?: string;
    label: string;
    cost: number;
    split: CreateExpenseRequest["items"][number]["split"];
  }>;
}

/**
 * Create-mode prefill from a receipt scan. Ignored in edit mode. Item amounts
 * are major-unit decimal strings (the form's native input format); each item
 * defaults to an EQUAL split across all participants. `lowConfidenceFields`
 * marks cells the scan was unsure about so the form can highlight them.
 */
export interface ExpenseFormInitialDraft {
  label?: string;
  currency?: SupportedCurrencyCode;
  items?: Array<{ label: string; amount: string }>;
  lowConfidenceFields?: string[];
  /**
   * Translation of the scanned receipt into the group's language (issue #284),
   * present only when the receipt was in another language. The original text
   * stays in `label`/`items`; this carries the translated copy so the form can
   * show both. `items` is index-aligned with `items` above.
   */
  translation?: {
    sourceLanguage?: string;
    targetLanguage: string;
    label?: string;
    items?: string[];
  };
}

interface ExpenseFormProps {
  group: Group;
  /** When provided, the form starts in "edit" mode seeded from this expense. */
  expense?: Expense;
  /**
   * Create-mode prefill from a receipt scan (see {@link ExpenseFormInitialDraft}).
   * Ignored when `expense` is set.
   */
  initialDraft?: ExpenseFormInitialDraft;
  /**
   * Participant id to default the sole "paid by" row to in create mode —
   * normally the current user (the person recording the expense). Falls back
   * to the first participant when omitted or not found. Ignored in edit mode,
   * where payers are seeded from the existing expense.
   */
  defaultPayerId?: string;
  submitLabel: string;
  pendingLabel: string;
  submitting: boolean;
  serverError?: string | null;
  onCancel: () => void;
  onSubmit: (values: ExpenseFormSubmit) => void | Promise<void>;
  /**
   * Optional additional UI rendered above the submit row. Used by the
   * recurring-expense form to inline its schedule editor; keeps the
   * submit-disabled gate unified with the rest of the form. Pass an
   * `extraValid` boolean to extend the disable-on-invalid signal.
   */
  extraFields?: ReactNode;
  extraValid?: boolean;
}

/**
 * Multi-item, multi-payer expense form. Mirrors the API contract:
 * - Each expense has a `currency`; payments carry both `amount` (expense
 *   currency minor units) and `charge` (group currency minor units). For
 *   same-currency expenses the two are equal; for cross-currency we
 *   apportion the user-supplied total charge across payments by `amount`,
 *   but only if the user actually changed payment amounts — otherwise the
 *   original per-payer `charge` values are passed through verbatim so
 *   description-only edits don't silently shift balances.
 * - Each item has its own split (EQUAL / AMOUNTS / PERCENTAGES / SHARES).
 * - `sum(payments.amount) === sum(items.cost)` is enforced client-side
 *   before submission so the server's 422 doesn't surface as a generic
 *   error.
 *
 * Currency cannot be changed in edit mode — `UpdateExpenseRequestSchema`
 * structurally omits the field, so the disabled CurrencyCombobox + the
 * defensive guard in handleSubmit reflect the contract, not a UI choice.
 *
 * The form is keyed by participant-id-set in the parent (so a group
 * refetch that adds/removes a participant remounts the form rather than
 * leaving stale `payerId`s pointing at no-longer-existing options).
 */
export function ExpenseForm({
  group,
  expense,
  initialDraft,
  defaultPayerId,
  submitLabel,
  pendingLabel,
  submitting,
  serverError,
  onCancel,
  onSubmit,
  extraFields,
  extraValid = true,
}: ExpenseFormProps): ReactNode {
  // Prefill applies only in create mode; an explicit `expense` always wins.
  const draft = expense ? undefined : initialDraft;
  // Receipt translation (issue #284): when the scan translated the receipt
  // into the group's language, surface the translated label and item labels
  // under their originals so the user can verify both.
  const translation = draft?.translation;
  // Fields the scan flagged as low-confidence, so we nudge the user to check
  // them before saving (the whole point of human-in-the-loop review).
  const lowConfidence = new Set(draft?.lowConfidenceFields ?? []);
  const editing = expense !== undefined;
  // Default the payer to the current user when they're a participant, else
  // the first participant. Keeps "I paid for this" — the common case — a
  // zero-click default while still fully editable.
  const initialPayerId =
    defaultPayerId && group.participants.some((p) => p.id === defaultPayerId)
      ? defaultPayerId
      : (group.participants[0]?.id ?? "");
  const [label, setLabel] = useState(
    () => expense?.label ?? draft?.label ?? "",
  );
  const [currency, setCurrency] = useState<SupportedCurrencyCode>(
    () => expense?.currency ?? draft?.currency ?? group.currency,
  );
  const [items, setItems] = useState<ItemDraft[]>(() => {
    if (expense) return seedItems(group, expense);
    if (draft?.items && draft.items.length > 0) {
      // `translation.items` is index-aligned with `draft.items`, so the
      // translated label travels onto each seeded row by position.
      const translatedItems = draft.translation?.items;
      return draft.items.map((it, i) => ({
        ...makeItem(group),
        label: it.label,
        amount: it.amount,
        translatedLabel: translatedItems?.[i],
      }));
    }
    return [makeItem(group)];
  });
  const [payments, setPayments] = useState<PaymentDraft[]>(() =>
    expense ? seedPayments(expense) : [makePayment(initialPayerId)],
  );
  const [groupChargeTotal, setGroupChargeTotal] = useState(() => {
    if (!expense || expense.currency === group.currency) return "";
    const groupExp = getCurrencyExponent(group.currency);
    const sum = expense.payments.reduce((s, p) => s + p.charge, 0);
    return fromMinor(sum, groupExp);
  });
  // While true, the cross-currency group total is auto-converted from the
  // items total via the static FX table (a starting estimate). Flipped off the
  // moment the user edits the field, at which point their typed amount (their
  // bank's actual converted charge) stands. Starts on only in create mode —
  // an existing expense already has real per-payer charges to preserve.
  const [chargeAutoFilled, setChargeAutoFilled] = useState(!editing);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // While true (create mode, single untouched payer), the payer's amount
  // mirrors the items total so "I paid the full amount" needs no typing.
  // Flipped off the moment the user edits a payment or adds a second payer.
  const [autoFillSolePayer, setAutoFillSolePayer] = useState(!editing);

  const expenseExponent = getCurrencyExponent(currency);
  const expenseStep = minorStepFor(expenseExponent);
  const groupExponent = getCurrencyExponent(group.currency);
  const groupStep = minorStepFor(groupExponent);
  const sameCurrency = currency === group.currency;

  const itemsTotalMinor = useMemo(
    () =>
      items.reduce((sum, item) => {
        const minor = toMinor(item.amount, expenseExponent);
        return sum + (Number.isFinite(minor) ? minor : 0);
      }, 0),
    [items, expenseExponent],
  );

  // Make "the creator paid the full amount" the default without an effect:
  // while the user hasn't touched payments (create mode, single payer), the
  // sole payer's amount is *derived* from the items total rather than stored.
  // `effectivePayments` is what the totals, validation, submit, and the
  // rendered input all read, so the displayed value and the submitted value
  // never diverge. Any manual edit flips `autoFillSolePayer` off (see
  // `updatePayment`), at which point the user's typed amounts stand.
  const autoFillSole =
    autoFillSolePayer && payments.length === 1 && itemsTotalMinor > 0;
  const effectivePayments = useMemo(
    () =>
      autoFillSole
        ? [
            {
              ...payments[0]!,
              amount: fromMinor(itemsTotalMinor, expenseExponent),
            },
          ]
        : payments,
    [autoFillSole, payments, itemsTotalMinor, expenseExponent],
  );

  const paymentsTotalMinor = useMemo(
    () =>
      effectivePayments.reduce((sum, p) => {
        const minor = toMinor(p.amount, expenseExponent);
        return sum + (Number.isFinite(minor) ? minor : 0);
      }, 0),
    [effectivePayments, expenseExponent],
  );

  // Cross-currency convenience: auto-convert the expense-currency total into
  // the group currency via the static FX table as a starting estimate. Like
  // `autoFillSole`, this is *derived* (not stored) while `chargeAutoFilled` is
  // on, so the shown value and the submitted value can't diverge; the first
  // manual edit flips the flag off and the user's typed amount stands. The
  // rates are an approximate snapshot (see USD_RATES) — the caption under the
  // field tells the user to edit it to their bank's actual charge.
  const suggestedChargeMinor = useMemo(
    () =>
      sameCurrency || paymentsTotalMinor <= 0
        ? 0
        : convertMinor(paymentsTotalMinor, currency, group.currency),
    [sameCurrency, paymentsTotalMinor, currency, group.currency],
  );
  const chargeIsAutoFilled =
    chargeAutoFilled && !sameCurrency && suggestedChargeMinor > 0;
  const effectiveGroupChargeTotal = chargeIsAutoFilled
    ? fromMinor(suggestedChargeMinor, groupExponent)
    : groupChargeTotal;

  // Used by the submit-button disabled gate so the user can't even attempt
  // a submit that would only surface the error post-click.
  const splitsValid = useMemo(
    () =>
      items.every(
        (it) =>
          it.divisions.some((d) => d.included) &&
          // For PERCENTAGES/SHARES, require integer entry on each included row.
          (it.splitType !== "PERCENTAGES" && it.splitType !== "SHARES"
            ? true
            : it.divisions
                .filter((d) => d.included)
                .every((d) => isPositiveInteger(d.value) || d.value === "")),
      ),
    [items],
  );

  const fundingMismatch =
    itemsTotalMinor > 0 && paymentsTotalMinor !== itemsTotalMinor;

  const payerIdsTaken = useMemo(
    () => new Set(payments.map((p) => p.payerId)),
    [payments],
  );

  function updateItem(key: string, patch: Partial<ItemDraft>): void {
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, ...patch } : it)),
    );
  }

  function removeItem(key: string): void {
    setItems((prev) =>
      prev.length === 1 ? prev : prev.filter((it) => it.key !== key),
    );
  }

  function addItem(): void {
    setItems((prev) => {
      const promoted =
        !editing && prev.length === 1 && !prev[0]!.label.trim()
          ? [{ ...prev[0]!, label: label.trim() }]
          : prev;
      return [...promoted, makeItem(group)];
    });
  }

  function updatePayment(key: string, patch: Partial<PaymentDraft>): void {
    // Any manual edit to a payment row means the user has taken over the
    // amounts — stop mirroring the items total into the sole payer.
    setAutoFillSolePayer(false);
    setPayments((prev) =>
      prev.map((p) => (p.key === key ? { ...p, ...patch } : p)),
    );
  }

  function removePayment(key: string): void {
    setAutoFillSolePayer(false);
    setPayments((prev) =>
      prev.length === 1 ? prev : prev.filter((p) => p.key !== key),
    );
  }

  function addPayment(): void {
    setAutoFillSolePayer(false);
    const next = group.participants.find((p) => !payerIdsTaken.has(p.id));
    // No un-taken participant means every participant already pays — the
    // Add-payer button is disabled, but defend in case it's reached.
    if (!next) return;
    setPayments((prev) => [...prev, makePayment(next.id)]);
  }

  /**
   * Switching the expense currency reinterprets entered amount strings
   * under the new exponent — '1.50' USD becomes 2 JPY. Clearing all
   * amount inputs on a currency change makes the reinterpretation
   * impossible. Splits and selected participants are preserved.
   */
  function onCurrencyChange(next: SupportedCurrencyCode): void {
    if (next === currency) return;
    setCurrency(next);
    setItems((prev) =>
      prev.map((it) => ({
        ...it,
        amount: "",
        // Per-division AMOUNTS values are also currency-scoped; clear them
        // so the user re-enters under the new exponent.
        divisions: it.divisions.map((d) =>
          it.splitType === "AMOUNTS" ? { ...d, value: "" } : d,
        ),
      })),
    );
    setPayments((prev) => prev.map((p) => ({ ...p, amount: "" })));
    setGroupChargeTotal("");
  }

  /**
   * Decide whether to re-apportion `charge` across payments.
   *
   * If we're in edit mode and every payment row matches its original
   * `participantId`/`amount` AND the user hasn't changed the group-charge
   * total, pass the original per-payer `charge` values through unchanged.
   * That keeps description-only edits from rewriting balances when the
   * historical FX wasn't strictly proportional.
   */
  function buildPayments(): CreateExpenseRequest["payments"] | string {
    // Use the auto-filled view so the submitted amounts match what the user
    // sees. In edit mode `effectivePayments === payments`, so the
    // cross-currency "unchanged" detection below is unaffected.
    const src = effectivePayments;
    if (group.participants.length === 0) {
      return "Add at least one participant to the group before recording an expense.";
    }
    if (src.some((p) => !p.payerId)) {
      return "Pick a payer for every payment row.";
    }
    const uniquePayers = new Set(src.map((p) => p.payerId));
    if (uniquePayers.size !== src.length) {
      return "Each payer can only appear once. Remove the duplicate.";
    }

    const minorAmounts = src.map((p) => toMinor(p.amount, expenseExponent));
    if (minorAmounts.some((m) => !Number.isFinite(m) || m <= 0)) {
      return "Each payer needs a positive amount.";
    }
    const totalAmount = minorAmounts.reduce((s, m) => s + m, 0);

    let charges: number[];
    if (sameCurrency) {
      charges = minorAmounts.slice();
    } else {
      // Read the effective value: the auto-converted estimate while untouched,
      // or the user's overriding amount once they've edited it.
      const totalCharge = toMinor(effectiveGroupChargeTotal, groupExponent);
      if (!Number.isFinite(totalCharge) || totalCharge <= 0) {
        return `Enter the total in ${group.currency} for the group.`;
      }

      const paymentsUnchanged = src.every(
        (p, i) =>
          p.originalAmount !== undefined &&
          minorAmounts[i] === p.originalAmount &&
          // payerId must match too — re-ordered rows count as changed.
          expense?.payments[i]?.participantId === p.payerId,
      );
      const originalChargeSum = src.reduce(
        (s, p) => s + (p.originalCharge ?? 0),
        0,
      );
      if (
        paymentsUnchanged &&
        totalCharge === originalChargeSum &&
        src.every((p) => p.originalCharge !== undefined)
      ) {
        // No payment-level change → preserve original per-payer charge.
        charges = src.map((p) => p.originalCharge!);
      } else {
        // Apportion charge across payers proportionally to amount, then put
        // any rounding remainder on the last payer so sum(charge) is exact.
        // BigInt avoids precision loss when amount * totalCharge would
        // exceed Number.MAX_SAFE_INTEGER on high-precision currencies.
        const totalChargeBig = BigInt(totalCharge);
        const totalAmountBig = BigInt(totalAmount);
        charges = minorAmounts.map((m) =>
          Number((BigInt(m) * totalChargeBig) / totalAmountBig),
        );
        const remainder = totalCharge - charges.reduce((s, c) => s + c, 0);
        charges[charges.length - 1] = (charges.at(-1) ?? 0) + remainder;
      }
    }

    return src.map((p, i) => ({
      participantId: p.payerId,
      amount: minorAmounts[i]!,
      charge: charges[i]!,
    }));
  }

  function buildItems(): ExpenseFormSubmit["items"] | string {
    const built: ExpenseFormSubmit["items"] = [];
    const multipleItems = items.length > 1;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      const cost = toMinor(item.amount, expenseExponent);
      if (!Number.isFinite(cost) || cost <= 0) {
        return `Item ${i + 1} needs a positive amount.`;
      }
      const split = buildSplit(item, cost, expenseExponent);
      if (typeof split === "string") return `Item ${i + 1}: ${split}`;
      // For multi-item expenses every row must carry its own description so
      // the receipt UI doesn't show several rows with the same name. Single-
      // item expenses can fall back to the parent label as a convenience.
      const trimmed = item.label.trim();
      if (multipleItems && !trimmed) {
        return `Item ${i + 1} needs a description.`;
      }
      const built1: ExpenseFormSubmit["items"][number] = {
        label: trimmed || label.trim(),
        cost,
        split,
      };
      if (item.id) built1.id = item.id;
      built.push(built1);
    }
    return built;
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(null);

    if (editing && expense && currency !== expense.currency) {
      // Defensive: the CurrencyCombobox is disabled in edit mode and
      // UpdateExpenseRequestSchema omits `currency`, but if either
      // protection regresses, refuse to submit a currency-mutating edit
      // rather than silently corrupting the expense.
      setSubmitError("Currency cannot be changed after an expense is created.");
      return;
    }
    if (!label.trim()) {
      setSubmitError("Description is required.");
      return;
    }
    const builtPayments = buildPayments();
    if (typeof builtPayments === "string") {
      setSubmitError(builtPayments);
      return;
    }
    const builtItems = buildItems();
    if (typeof builtItems === "string") {
      setSubmitError(builtItems);
      return;
    }
    const totalAmount = builtPayments.reduce((s, p) => s + p.amount, 0);
    const totalCost = builtItems.reduce((s, it) => s + it.cost, 0);
    if (totalAmount !== totalCost) {
      setSubmitError(
        "Payment total must match the items total. Adjust amounts so they balance.",
      );
      return;
    }

    await onSubmit({
      label: label.trim(),
      currency,
      payments: builtPayments,
      items: builtItems,
    });
  }

  if (group.participants.length === 0) {
    return (
      <div className="rounded-md bg-surface-container-low p-5 text-body-md text-on-surface-variant">
        Add at least one participant to this group before recording an expense.
      </div>
    );
  }

  const submitDisabled =
    submitting ||
    !label.trim() ||
    items.length === 0 ||
    !splitsValid ||
    payments.length === 0 ||
    !extraValid;

  return (
    <form className="space-y-stack-lg" onSubmit={handleSubmit}>
      <Section title="Details" description="What was this expense for?">
        <div className="space-y-2">
          <Label htmlFor="label">Description</Label>
          <Input
            id="label"
            placeholder="Dinner at Padella"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            minLength={1}
            maxLength={120}
            className={
              lowConfidence.has("label")
                ? "ring-1 ring-amber-500/60"
                : undefined
            }
          />
          {lowConfidence.has("label") ? <LowConfidenceHint /> : null}
          {translation?.label ? (
            <TranslationHint
              text={translation.label}
              sourceLanguage={translation.sourceLanguage}
              targetLanguage={translation.targetLanguage}
            />
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <CurrencyCombobox
            id="currency"
            value={currency}
            onChange={onCurrencyChange}
            disabled={editing}
          />
          {lowConfidence.has("currency") ? <LowConfidenceHint /> : null}
          {editing ? (
            <p className="text-label-sm text-on-surface-variant">
              Currency can't be changed after an expense is created.
            </p>
          ) : !sameCurrency ? (
            <p className="text-label-sm text-on-surface-variant">
              Group is in {group.currency}. Enter the total below in{" "}
              {group.currency} so balances stay in the group's currency.
            </p>
          ) : null}
        </div>
      </Section>

      <ItemsEditor
        group={group}
        items={items}
        simpleSingleItem={!editing && items.length === 1}
        currency={currency}
        exponent={expenseExponent}
        step={expenseStep}
        translationSourceLanguage={translation?.sourceLanguage}
        translationTargetLanguage={translation?.targetLanguage}
        updateItem={updateItem}
        addItem={addItem}
        removeItem={removeItem}
      />

      <PaymentsEditor
        group={group}
        payments={effectivePayments}
        currency={currency}
        step={expenseStep}
        updatePayment={updatePayment}
        addPayment={addPayment}
        removePayment={removePayment}
        fundingMismatch={fundingMismatch}
        itemsTotalMinor={itemsTotalMinor}
        paymentsTotalMinor={paymentsTotalMinor}
        exponent={expenseExponent}
        payerIdsTaken={payerIdsTaken}
      />

      {!sameCurrency ? (
        <Section
          title={`Group total (${group.currency})`}
          description={`Apportioned across payers in proportion to their ${currency} amount unless you change a payment, in which case existing charges are preserved.`}
        >
          <div className="space-y-2">
            <Label htmlFor="charge-total">
              Total in group currency ({group.currency})
            </Label>
            <Input
              id="charge-total"
              type="number"
              inputMode="decimal"
              step={groupStep}
              min={groupStep}
              placeholder={(0).toFixed(groupExponent)}
              value={effectiveGroupChargeTotal}
              onChange={(e) => {
                // First edit hands control to the user: stop auto-converting
                // and keep whatever they type (their bank's actual charge).
                setChargeAutoFilled(false);
                setGroupChargeTotal(e.target.value);
              }}
              required
            />
            {chargeIsAutoFilled ? (
              <p className="text-label-sm text-on-surface-variant">
                {approxRateNote(currency, group.currency)} Edit to match your
                bank's actual charge.
              </p>
            ) : null}
          </div>
        </Section>
      ) : null}

      {extraFields}

      {submitError ? (
        <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
          {submitError}
        </div>
      ) : null}
      {serverError && !submitError ? (
        <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
          {serverError}
        </div>
      ) : null}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitDisabled}>
          {submitting ? pendingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** Inline nudge under a field the receipt scan was unsure about. */
function LowConfidenceHint(): ReactNode {
  return (
    <p className="text-label-sm text-amber-600 dark:text-amber-500">
      Double-check this — we weren't sure we read it correctly.
    </p>
  );
}

/**
 * Display-only translation of a scanned field into the group's language (issue
 * #284), shown under the editable original so the user sees both. The original
 * stays in the input; this is informational and isn't submitted.
 */
function TranslationHint({
  text,
  sourceLanguage,
  targetLanguage,
}: {
  text: string;
  sourceLanguage?: string;
  targetLanguage?: string;
}): ReactNode {
  // Prefer "French → English"; fall back to just the target, or a generic
  // label when neither code is known.
  const target = targetLanguage ? languageName(targetLanguage) : undefined;
  const source = sourceLanguage ? languageName(sourceLanguage) : undefined;
  const prefix =
    source && target
      ? `${source} → ${target}`
      : target
        ? `Translated to ${target}`
        : "Translated";

  return (
    <p className="text-label-sm text-on-surface-variant">
      <span className="font-medium">{prefix}:</span> {text}
    </p>
  );
}

function ItemsEditor({
  group,
  items,
  simpleSingleItem,
  currency,
  exponent,
  step,
  translationSourceLanguage,
  translationTargetLanguage,
  updateItem,
  addItem,
  removeItem,
}: {
  group: Group;
  items: ItemDraft[];
  simpleSingleItem: boolean;
  currency: SupportedCurrencyCode;
  exponent: number;
  step: string;
  /** Scan translation languages (issue #284), threaded down to each row. */
  translationSourceLanguage?: string;
  translationTargetLanguage?: string;
  updateItem: (key: string, patch: Partial<ItemDraft>) => void;
  addItem: () => void;
  removeItem: (key: string) => void;
}): ReactNode {
  return (
    <Section
      title={simpleSingleItem ? "Amount & split" : "Items"}
      description={
        simpleSingleItem
          ? "Enter the total and choose who shares it."
          : "Add one or more items and choose how each is split."
      }
      action={
        <Button type="button" variant="secondary" size="sm" onClick={addItem}>
          <Plus className="size-4" />
          Add item
        </Button>
      }
    >
      <div className="space-y-3">
        {items.map((item, index) => (
          <ItemRow
            key={item.key}
            index={index}
            item={item}
            group={group}
            currency={currency}
            exponent={exponent}
            step={step}
            canRemove={items.length > 1}
            requireLabel={items.length > 1}
            simple={simpleSingleItem}
            translationSourceLanguage={translationSourceLanguage}
            translationTargetLanguage={translationTargetLanguage}
            onChange={(patch) => updateItem(item.key, patch)}
            onRemove={() => removeItem(item.key)}
          />
        ))}
      </div>
    </Section>
  );
}

function ItemRow({
  index,
  item,
  group,
  currency,
  exponent,
  step,
  canRemove,
  requireLabel,
  simple,
  translationSourceLanguage,
  translationTargetLanguage,
  onChange,
  onRemove,
}: {
  index: number;
  item: ItemDraft;
  group: Group;
  currency: SupportedCurrencyCode;
  exponent: number;
  step: string;
  canRemove: boolean;
  requireLabel: boolean;
  simple: boolean;
  /** Scan translation languages (issue #284) for this row's translated label. */
  translationSourceLanguage?: string;
  translationTargetLanguage?: string;
  onChange: (patch: Partial<ItemDraft>) => void;
  onRemove: () => void;
}): ReactNode {
  const labelId = `item-${item.key}-label`;
  const amountId = `item-${item.key}-amount`;
  const typeId = `item-${item.key}-type`;
  const includedCount = item.divisions.filter((d) => d.included).length;

  function updateDivision(
    participantId: string,
    patch: Partial<DivisionDraft>,
  ): void {
    onChange({
      divisions: item.divisions.map((d) =>
        d.participantId === participantId ? { ...d, ...patch } : d,
      ),
    });
  }

  return (
    <Card tone="neutral" className="border border-outline-variant/50">
      <CardContent className="space-y-4 pt-5">
        {!simple ? (
          <div className="flex items-center justify-between gap-3 border-b border-outline-variant/40 pb-3">
            <p className="grid size-7 place-items-center rounded-full bg-primary-fixed text-label-sm text-on-primary-fixed-variant">
              {index + 1}
            </p>
            {canRemove ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove item ${index + 1}`}
                onClick={onRemove}
              >
                <Trash2 className="size-4" />
              </Button>
            ) : null}
          </div>
        ) : null}

        <div
          className={
            simple ? "space-y-2" : "grid gap-3 sm:grid-cols-[1fr_140px]"
          }
        >
          {!simple ? (
            <div className="space-y-2">
              <Label htmlFor={labelId}>Description</Label>
              <Input
                id={labelId}
                placeholder="Pasta"
                value={item.label}
                onChange={(e) => onChange({ label: e.target.value })}
                maxLength={200}
                required={requireLabel}
              />
              {item.translatedLabel ? (
                <TranslationHint
                  text={item.translatedLabel}
                  sourceLanguage={translationSourceLanguage}
                  targetLanguage={translationTargetLanguage}
                />
              ) : null}
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor={amountId}>Amount ({currency})</Label>
            <Input
              id={amountId}
              type="number"
              inputMode="decimal"
              step={step}
              min={step}
              placeholder={(0).toFixed(exponent)}
              value={item.amount}
              onChange={(e) => onChange({ amount: e.target.value })}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={typeId}>Split</Label>
          <select
            id={typeId}
            value={item.splitType}
            onChange={(e) =>
              onChange({ splitType: e.target.value as SplitType })
            }
            className="flex h-11 w-full rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <option value="EQUAL">Equally</option>
            <option value="AMOUNTS">By exact amounts</option>
            <option value="PERCENTAGES">By percentages</option>
            <option value="SHARES">By shares</option>
          </select>
        </div>

        <div className="space-y-3 rounded-sm bg-surface-container-low p-3">
          <p className="px-1 text-label-sm text-on-surface-variant">
            Who's included
          </p>
          {/* Same avatar-pill style as "Paid by", but multi-select: tap to
              include/exclude each person. */}
          <AvatarMultiPicker
            label="Who's included"
            people={group.participants}
            isSelected={(id) =>
              item.divisions.find((d) => d.participantId === id)?.included ??
              false
            }
            onToggle={(id, included) => updateDivision(id, { included })}
          />
          {/* For non-equal splits each included person needs a value; show a
              compact labelled input per included participant. */}
          {item.splitType !== "EQUAL" ? (
            <div className="space-y-1.5">
              {group.participants.map((p) => {
                const division = item.divisions.find(
                  (d) => d.participantId === p.id,
                );
                if (!division || !division.included) return null;
                return (
                  <DivisionValueRow
                    key={p.id}
                    participantName={p.displayName}
                    division={division}
                    splitType={item.splitType}
                    currency={currency}
                    step={step}
                    exponent={exponent}
                    onChange={(patch) => updateDivision(p.id, patch)}
                  />
                );
              })}
            </div>
          ) : null}
          {includedCount === 0 ? (
            <p className="px-1 text-label-sm text-error">
              Pick at least one participant for this split.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Leading circle of a selectable person pill: a tick when selected (clearer at
 * a glance than initials), otherwise the person's avatar. The tick circle is
 * sized to match the `sm` Avatar so the pill doesn't reflow on toggle.
 */
function PillLeading({
  name,
  selected,
}: {
  name: string;
  selected: boolean;
}): ReactNode {
  if (selected) {
    return (
      <span
        aria-hidden="true"
        className="grid size-8 shrink-0 place-items-center rounded-full bg-on-primary/20 text-on-primary"
      >
        <Check className="size-4" strokeWidth={3} />
      </span>
    );
  }
  return <Avatar name={name} size="sm" />;
}

/**
 * Multi-select person picker: the same avatar+name pills as the "Paid by"
 * {@link AvatarPersonPicker}, but each toggles independently (include/exclude)
 * rather than acting as a radio. Used for an item's split membership.
 */
function AvatarMultiPicker({
  label,
  people,
  isSelected,
  onToggle,
}: {
  label: string;
  people: Group["participants"];
  isSelected: (personId: string) => boolean;
  onToggle: (personId: string, included: boolean) => void;
}): ReactNode {
  return (
    <div aria-label={label} className="flex flex-wrap gap-2">
      {people.map((p) => {
        const selected = isSelected(p.id);
        return (
          <button
            key={p.id}
            type="button"
            role="checkbox"
            aria-checked={selected}
            aria-label={p.displayName}
            onClick={() => onToggle(p.id, !selected)}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-label-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              selected
                ? "border-primary bg-primary text-on-primary"
                : "border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container",
            )}
          >
            <PillLeading name={p.displayName} selected={selected} />
            <span className="max-w-[8rem] truncate">{p.displayName}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Per-participant value input for a non-equal split (exact amount / percentage
 * / shares). Only rendered for participants already included via the pills
 * above, so it carries just the labelled input, not an include toggle.
 */
function DivisionValueRow({
  participantName,
  division,
  splitType,
  currency,
  step,
  exponent,
  onChange,
}: {
  participantName: string;
  division: DivisionDraft;
  splitType: SplitType;
  currency: SupportedCurrencyCode;
  step: string;
  exponent: number;
  onChange: (patch: Partial<DivisionDraft>) => void;
}): ReactNode {
  const integerInvalid =
    (splitType === "PERCENTAGES" || splitType === "SHARES") &&
    division.value !== "" &&
    !isPositiveInteger(division.value);

  return (
    <div className="flex flex-col gap-1 rounded-sm bg-surface-container-lowest px-4 py-2 text-body-md">
      <div className="flex items-center gap-3">
        <span className="flex flex-1 items-center gap-2">
          <Avatar name={participantName} size="sm" />
          {participantName}
          {division.isLateJoiner ? (
            <span
              className="rounded-full bg-tertiary-fixed px-2 text-label-sm text-on-tertiary-fixed"
              title="Joined the group after this expense was created"
            >
              new
            </span>
          ) : null}
        </span>
        {splitType === "AMOUNTS" ? (
          <Input
            type="number"
            inputMode="decimal"
            step={step}
            min="0"
            placeholder={(0).toFixed(exponent)}
            value={division.value}
            onChange={(e) => onChange({ value: e.target.value })}
            aria-label={`${participantName} amount in ${currency}`}
            className="h-9 w-32"
          />
        ) : null}
        {splitType === "PERCENTAGES" ? (
          <Input
            type="number"
            inputMode="numeric"
            step="1"
            min="0"
            max="100"
            placeholder="0"
            value={division.value}
            onChange={(e) => onChange({ value: e.target.value })}
            aria-label={`${participantName} percentage`}
            aria-invalid={integerInvalid}
            className="h-9 w-24"
          />
        ) : null}
        {splitType === "SHARES" ? (
          <Input
            type="number"
            inputMode="numeric"
            step="1"
            min="0"
            placeholder="1"
            value={division.value}
            onChange={(e) => onChange({ value: e.target.value })}
            aria-label={`${participantName} shares`}
            aria-invalid={integerInvalid}
            className="h-9 w-24"
          />
        ) : null}
      </div>
      {integerInvalid ? (
        <p className="pl-7 text-label-sm text-error">Whole numbers only.</p>
      ) : null}
    </div>
  );
}

/**
 * Single-select person picker for small groups: a horizontal, wrapping row of
 * avatar+name chips acting as radio buttons. `people` is pre-filtered by the
 * caller (e.g. to drop payers already taken by another row).
 */
function AvatarPersonPicker({
  label,
  value,
  people,
  onChange,
}: {
  label: string;
  value: string;
  people: Group["participants"];
  onChange: (personId: string) => void;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex flex-1 flex-wrap gap-2"
    >
      {people.map((p) => {
        const selected = p.id === value;
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(p.id)}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-label-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              selected
                ? "border-primary bg-primary text-on-primary"
                : "border-outline-variant/60 bg-surface-container-lowest text-on-surface hover:bg-surface-container",
            )}
          >
            <PillLeading name={p.displayName} selected={selected} />
            <span className="max-w-[8rem] truncate">{p.displayName}</span>
          </button>
        );
      })}
    </div>
  );
}

function PaymentsEditor({
  group,
  payments,
  currency,
  step,
  updatePayment,
  addPayment,
  removePayment,
  fundingMismatch,
  itemsTotalMinor,
  paymentsTotalMinor,
  exponent,
  payerIdsTaken,
}: {
  group: Group;
  payments: PaymentDraft[];
  currency: SupportedCurrencyCode;
  step: string;
  updatePayment: (key: string, patch: Partial<PaymentDraft>) => void;
  addPayment: () => void;
  removePayment: (key: string) => void;
  fundingMismatch: boolean;
  itemsTotalMinor: number;
  paymentsTotalMinor: number;
  exponent: number;
  payerIdsTaken: Set<string>;
}): ReactNode {
  const minorPerMajor = 10 ** exponent;
  const fmt = (m: number): string => (m / minorPerMajor).toFixed(exponent);
  // Small groups get a tappable avatar+name picker instead of a dropdown.
  const compactPeople = group.participants.length <= COMPACT_PEOPLE_MAX;

  return (
    <Section
      title="Paid by"
      description="Record who paid and how much. The total must match the items."
      action={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addPayment}
          disabled={payments.length >= group.participants.length}
        >
          <Plus className="size-4" />
          Add payer
        </Button>
      }
    >
      <div className="space-y-2">
        {payments.map((payment, index) => (
          <div
            key={payment.key}
            className="flex items-center gap-3 rounded-sm bg-surface-container-low px-4 py-2"
          >
            {compactPeople ? (
              <AvatarPersonPicker
                label={`Payer ${index + 1}`}
                value={payment.payerId}
                onChange={(payerId) => updatePayment(payment.key, { payerId })}
                people={group.participants.filter(
                  (p) => !payerIdsTaken.has(p.id) || p.id === payment.payerId,
                )}
              />
            ) : (
              <select
                value={payment.payerId}
                onChange={(e) =>
                  updatePayment(payment.key, { payerId: e.target.value })
                }
                className="h-9 flex-1 rounded-sm bg-surface-container-lowest px-3 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={`Payer ${index + 1}`}
              >
                {group.participants.map((p) => {
                  // Filter out participants picked by other rows so duplicate
                  // payerIds can't be saved.
                  const takenByOther =
                    payerIdsTaken.has(p.id) && p.id !== payment.payerId;
                  if (takenByOther) return null;
                  return (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  );
                })}
              </select>
            )}
            <Input
              type="number"
              inputMode="decimal"
              step={step}
              min={step}
              placeholder={(0).toFixed(exponent)}
              value={payment.amount}
              onChange={(e) =>
                updatePayment(payment.key, { amount: e.target.value })
              }
              aria-label={`Payer ${index + 1} amount in ${currency}`}
              className="h-9 w-32"
              required
            />
            {payments.length > 1 ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove payer ${index + 1}`}
                onClick={() => removePayment(payment.key)}
              >
                <Trash2 className="size-4" />
              </Button>
            ) : null}
          </div>
        ))}
      </div>
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-2 rounded-sm px-4 py-3 text-label-md",
          fundingMismatch
            ? "bg-error-container text-on-error-container"
            : "bg-surface-container text-on-surface-variant",
        )}
      >
        <span>
          Items {fmt(itemsTotalMinor)} {currency} · Paid{" "}
          {fmt(paymentsTotalMinor)} {currency}
        </span>
        {fundingMismatch ? (
          <span className="font-bold">These must match before saving.</span>
        ) : (
          <span className="font-bold text-tertiary">Balanced</span>
        )}
      </div>
    </Section>
  );
}

/**
 * Build the API split payload for a single item, validating that the user
 * input is internally consistent (sum of percentages = 100, sum of amounts
 * = cost, at least one participant included). Returns an error string for
 * the form when invalid.
 */
function buildSplit(
  item: ItemDraft,
  cost: number,
  exponent: number,
): CreateExpenseRequest["items"][number]["split"] | string {
  const included = item.divisions.filter((d) => d.included);
  if (included.length === 0) {
    return "Pick at least one participant for the split.";
  }

  switch (item.splitType) {
    case "EQUAL":
      return {
        type: "EQUAL",
        divisions: included.map((d) => ({ participantId: d.participantId })),
      };
    case "AMOUNTS": {
      // Empty input is a missing entry, not an implicit zero — the previous
      // behaviour `Number('') === 0` was indistinguishable from a deliberate
      // 0, which made "you forgot a row" look like "amounts don't sum".
      const missing = included.find((d) => d.value.trim() === "");
      if (missing) {
        return "Enter an amount for every included participant.";
      }
      const divisions = included.map((d) => ({
        participantId: d.participantId,
        amount: toMinor(d.value, exponent),
      }));
      if (divisions.some((d) => !Number.isFinite(d.amount) || d.amount < 0)) {
        return "Each amount must be zero or positive.";
      }
      const total = divisions.reduce((s, d) => s + d.amount, 0);
      if (total !== cost) {
        return `Amounts must sum to the item total (${(cost / 10 ** exponent).toFixed(exponent)}).`;
      }
      return { type: "AMOUNTS", divisions };
    }
    case "PERCENTAGES": {
      const nonInteger = included.find(
        (d) => d.value !== "" && !isPositiveInteger(d.value),
      );
      if (nonInteger) {
        return "Percentages must be whole numbers.";
      }
      const divisions = included.map((d) => ({
        participantId: d.participantId,
        value: Number.parseInt(d.value || "0", 10),
      }));
      if (
        divisions.some(
          (d) => !Number.isFinite(d.value) || d.value < 0 || d.value > 100,
        )
      ) {
        return "Each percentage must be between 0 and 100.";
      }
      const total = divisions.reduce((s, d) => s + d.value, 0);
      if (total !== 100) {
        return `Percentages must sum to 100 (currently ${total}).`;
      }
      return { type: "PERCENTAGES", divisions };
    }
    case "SHARES": {
      const nonInteger = included.find(
        (d) => d.value !== "" && !isPositiveInteger(d.value),
      );
      if (nonInteger) {
        return "Shares must be whole numbers.";
      }
      const divisions = included.map((d) => ({
        participantId: d.participantId,
        value: Number.parseInt(d.value || "0", 10),
      }));
      if (divisions.some((d) => !Number.isFinite(d.value) || d.value < 0)) {
        return "Each share count must be a non-negative integer.";
      }
      if (divisions.every((d) => d.value === 0)) {
        return "At least one participant needs a non-zero share.";
      }
      return { type: "SHARES", divisions };
    }
  }
}
